// One territory in the list (iOS TerritoryCard): color capsule, name, "Inactive", kind with its area or ZIP codes,
// assigned reps, and the four stats (stores, visits in 30 days, won, open pipeline). The whole card is one link to the
// detail page with a one-sentence accessible name, so a keyboard user tabs once per territory.

import { CircleDollarSign, Footprints, Hash, Lasso, Map as MapIcon, MapPin, Trophy } from 'lucide-react';
import { memo, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { DistanceUnit, TerritoryKind } from '@/contracts/enums';
import type { TerritoryPayload } from '@/contracts/models';
import { formatCompactMoney, formatGrouped } from '@/domain/format';
import type { TerritoryStats } from '@/domain/territories';
import { AvatarStack, Badge, Chip, cardClass, cn, normalizeHex, withAlpha, type AvatarPerson } from '@/ui';
import { territoryKindText, territoryName } from './territoryModel';

/** `TerritoryKind.systemImage`: lasso, number, map. */
export function TerritoryKindIcon({ kind, className }: { kind: TerritoryKind; className?: string }) {
  const Icon = kind === 'custom' ? Lasso : kind === 'postalCodes' ? Hash : MapIcon;
  return <Icon aria-hidden className={className} />;
}

export interface TerritoryCardProps {
  territory: TerritoryPayload;
  stats: TerritoryStats;
  /** The assigned people that exist, in the territory's order. */
  reps: readonly AvatarPerson[];
  unit: DistanceUnit;
  currencyCode: string;
  /** Written by a newer app: shown read-only with a badge. */
  newer?: boolean;
}

function TerritoryCardImpl({ territory: t, stats, reps, unit, currencyCode, newer = false }: TerritoryCardProps) {
  const name = territoryName(t);
  // Synced colors are free text: only a parsed hex goes into inline styles (gray otherwise, like iOS).
  const color = normalizeHex(t.colorHex);
  const kindText = territoryKindText(t, unit);
  const pipeline = formatCompactMoney(stats.pipelineValue, currencyCode);
  const label = [
    name,
    t.isActive ? null : 'inactive',
    newer ? 'created by a newer app' : null,
    kindText,
    `${formatGrouped(stats.records)} ${stats.records === 1 ? 'store' : 'stores'}`,
    `${formatGrouped(stats.visits30d)} ${stats.visits30d === 1 ? 'visit' : 'visits'} in 30 days`,
    `${formatGrouped(stats.won)} won`,
    `${pipeline} open pipeline`,
    reps.length > 0 ? `assigned to ${reps.map((r) => r.name).join(', ')}` : 'no rep assigned',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Link
      to={`/territories/${t.id}`}
      aria-label={label}
      className={cn(
        cardClass,
        'flex flex-col gap-3 p-4 transition-[box-shadow,transform] duration-150 hover:shadow-raised active:scale-[0.995]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        !t.isActive && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="h-11 w-2 shrink-0 rounded-full"
          style={{ background: `linear-gradient(180deg, ${color}, color-mix(in srgb, ${color} 75%, black))`, boxShadow: `0 2px 4px ${withAlpha(color, 0.35)}` }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="truncate text-[15px] font-semibold text-fg">{name}</h3>
            {!t.isActive && (
              <Chip color="#8C919E" size="xs">
                Inactive
              </Chip>
            )}
            {newer && (
              <Badge tone="warning" size="xs" title="Created by a newer version of Ground Team. Update the app to edit it.">
                Newer app
              </Badge>
            )}
          </div>
          <p className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-fg-muted">
            <TerritoryKindIcon kind={t.kind} className="size-3 shrink-0" />
            <span className="truncate">{kindText}</span>
          </p>
        </div>
        <AvatarStack people={reps} size={26} emptyLabel="No rep" />
      </div>
      <div className="grid grid-cols-4 gap-2 border-t border-line pt-3">
        <StatCell icon={<MapPin />} tint={color} value={formatGrouped(stats.records)} label={stats.records === 1 ? 'Store' : 'Stores'} />
        <StatCell icon={<Footprints />} tint="#F96A3C" value={formatGrouped(stats.visits30d)} label="Visits 30d" />
        <StatCell icon={<Trophy />} tint="#29A85C" value={formatGrouped(stats.won)} label="Won" />
        <StatCell icon={<CircleDollarSign />} tint="var(--gt-accent)" value={pipeline} label="Pipeline" />
      </div>
    </Link>
  );
}

export const TerritoryCard = memo(TerritoryCardImpl);

function StatCell({ icon, tint, value, label }: { icon: ReactNode; tint: string; value: string; label: string }) {
  return (
    <div aria-hidden className="min-w-0">
      <div className="flex items-center gap-1">
        <span className="inline-flex shrink-0 [&_svg]:size-3" style={{ color: tint }}>
          {icon}
        </span>
        <span className="truncate font-rounded text-sm font-bold text-fg tabular-nums">{value}</span>
      </div>
      <p className="truncate text-[9px] font-semibold tracking-[0.04em] text-fg-muted uppercase">{label}</p>
    </div>
  );
}
